/**
 * Buyer-side G&A overhead categories (D3.6 / SPEC-89).
 * Desk judgment overlays — not seller-provided facts unless sourced.
 * Legacy overheadInCf / overheadMonthly map to a single rolled `other` category.
 */

export const BUYER_OH_SCHEMA = 'buyerOverhead.v1';

/** Internal illustration only until sourced/mapped — do not treat as seller fact. */
export const ILLUSTRATIVE_BUYER_OH_MONTHLY = 53000;

export const DEFAULT_BUYER_OH_CATEGORIES = [
  { id: 'fieldOps', label: 'Field ops', monthly: 0, includeInCf: false },
  { id: 'backOffice', label: 'Back office', monthly: 0, includeInCf: false },
  { id: 'mgmt', label: 'Management', monthly: 0, includeInCf: false },
  { id: 'other', label: 'Other / rolled', monthly: 0, includeInCf: false },
];

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Normalize assumptions into a versioned buyer-overhead structure.
 * Prefer explicit categories; otherwise roll legacy scalar into `other`.
 */
export function normalizeBuyerOverhead(assumptions = {}) {
  const raw =
    assumptions.buyerOverheadCategories ||
    assumptions.buyerOverhead?.categories ||
    null;
  const hasExplicit = Array.isArray(raw) && raw.length > 0;

  let categories;
  if (hasExplicit) {
    const byId = new Map();
    for (const def of DEFAULT_BUYER_OH_CATEGORIES) {
      const found = Array.isArray(raw) ? raw.find((c) => c && c.id === def.id) : null;
      byId.set(def.id, {
        id: def.id,
        label: found?.label || def.label,
        monthly: Math.max(0, num(found?.monthly, def.monthly)),
        includeInCf: !!(found?.includeInCf ?? def.includeInCf),
      });
    }
    for (const c of raw) {
      if (!c?.id || byId.has(c.id)) continue;
      byId.set(c.id, {
        id: String(c.id),
        label: c.label || String(c.id),
        monthly: Math.max(0, num(c.monthly, 0)),
        includeInCf: !!c.includeInCf,
      });
    }
    categories = [...byId.values()];
  } else {
    const legacyMonthly = Math.max(0, num(assumptions.overheadMonthly, 0));
    const legacyIn = !!assumptions.overheadInCf;
    categories = DEFAULT_BUYER_OH_CATEGORIES.map((def) =>
      def.id === 'other'
        ? { ...def, monthly: legacyMonthly, includeInCf: legacyIn }
        : { ...def },
    );
  }

  const monthlyTotal = categories.reduce((s, c) => s + c.monthly, 0);
  let monthlyIncluded = categories
    .filter((c) => c.includeInCf)
    .reduce((s, c) => s + c.monthly, 0);

  // Stress / legacy force: overheadInCf true with no category flags → apply total.
  let forcedAll = false;
  if (
    !!assumptions.overheadInCf &&
    monthlyIncluded === 0 &&
    monthlyTotal > 0 &&
    !categories.some((c) => c.includeInCf)
  ) {
    monthlyIncluded = monthlyTotal;
    forcedAll = true;
  }

  return {
    schemaVersion: BUYER_OH_SCHEMA,
    categories,
    monthlyTotal,
    monthlyIncluded,
    overheadMonthly: monthlyTotal,
    overheadInCf: monthlyIncluded > 0 || categories.some((c) => c.includeInCf) || forcedAll,
    forcedAll,
    hasExplicitCategories: hasExplicit,
    classification: 'Judgment',
    illustrationNote:
      Math.abs(monthlyTotal - ILLUSTRATIVE_BUYER_OH_MONTHLY) < 1
        ? 'Buyer G&A is an illustrative internal figure, not a seller fact. The dollar amount stays on the desk and is omitted from buyer- and investor-facing exports. Buyer G&A stops after the last month with positive operating cash flow.'
        : 'Buyer G&A categories are desk judgment, not seller facts. Buyer G&A stops after the last month with positive operating cash flow.',
  };
}

/** Drop the illustrative $53k/mo figure from buyer- and investor-facing exports. */
export function redactIllustrativeOverhead(oh) {
  if (!oh) return oh;
  const hit = (n) => Math.abs(Number(n) - ILLUSTRATIVE_BUYER_OH_MONTHLY) < 1;
  const categories = (oh.categories || []).map((c) =>
    hit(c.monthly) ? { ...c, monthly: null, redacted: true } : { ...c },
  );
  const redacted = categories.some((c) => c.redacted) || hit(oh.monthlyTotal) || hit(oh.monthlyIncluded);
  if (!redacted) return oh;
  return {
    ...oh,
    categories,
    monthlyTotal: hit(oh.monthlyTotal) ? null : oh.monthlyTotal,
    monthlyIncluded: hit(oh.monthlyIncluded) ? null : oh.monthlyIncluded,
    redacted: true,
    illustrationNote:
      'Buyer G&A is illustrative desk judgment. The internal monthly illustration is omitted from this export. Buyer G&A stops after the last month with positive operating cash flow.',
  };
}

export function scrubIllustrativeDollars(text) {
  return String(text ?? '').replace(/~?\$53k(?:\/mo)?|\$53,000(?:\/mo)?|\b53,000\b|\b53000\b/gi, 'an illustrative internal figure');
}

/** Force every category into CF (desk stress path). */
export function forceBuyerOverheadInCf(assumptions = {}) {
  const oh = normalizeBuyerOverhead(assumptions);
  const categories = oh.categories.map((c) => ({ ...c, includeInCf: true }));
  return {
    ...assumptions,
    buyerOverheadCategories: categories,
    overheadInCf: true,
    overheadMonthly: oh.monthlyTotal,
  };
}

/** Compact snapshot for freeze / export. */
export function pickBuyerOverheadSnapshot(assumptions = {}) {
  const oh = normalizeBuyerOverhead(assumptions);
  return {
    schemaVersion: oh.schemaVersion,
    categories: oh.categories.map((c) => ({
      id: c.id,
      label: c.label,
      monthly: +num(c.monthly).toFixed(2),
      includeInCf: !!c.includeInCf,
    })),
    monthlyTotal: +oh.monthlyTotal.toFixed(2),
    monthlyIncluded: +oh.monthlyIncluded.toFixed(2),
    classification: oh.classification,
  };
}

/** Seed UI category state from package assumptions (legacy-safe). */
export function categoriesFromAssumptions(assumptions = {}) {
  return normalizeBuyerOverhead(assumptions).categories.map((c) => ({ ...c }));
}

/** Derive legacy scalar mirrors from category state for older freezes / CRM. */
export function legacyOverheadFromCategories(categories) {
  const oh = normalizeBuyerOverhead({ buyerOverheadCategories: categories });
  return {
    overheadMonthly: oh.monthlyTotal,
    overheadInCf: oh.overheadInCf,
  };
}
